import type { ModelState } from '@/features/domaine/types/techniques'
import { objectMergeKeys } from '@/features/shared/tools'

export type MergeStateOptions<TStateA extends object, TStateB extends object> = {
  defaultA?: TStateA
  defaultB?: TStateB
  /** compute est toujours executé avant summarize */
  compute?: (state: TStateA & TStateB) => TStateA & TStateB
  summarize?: (sum: TStateA & TStateB, state: TStateA & TStateB) => TStateA & TStateB
}

export function mergeModelState<TModel extends object, TStateA extends object, TStateB extends object>(
  a: ModelState<TModel, TStateA>,
  b: ModelState<TModel, TStateB>,
  options: MergeStateOptions<TStateA, TStateB> = {},
): ModelState<TModel, TStateA & TStateB> {
  const defaultState: TStateA & TStateB = {
    ...(options?.defaultA ?? ({} as any)),
    ...(options?.defaultB ?? ({} as any)),
  } as const
  return mergeModelStateInt(a, b, defaultState, options)
}

function mergeModelStateInt<TModel, TStateA extends object, TStateB extends object>(
  a: ModelState<TModel, TStateA>,
  b: ModelState<TModel, TStateB>,
  defaultState: Readonly<TStateA & TStateB>,
  options?: MergeStateOptions<TStateA, TStateB>,
): ModelState<TModel, TStateA & TStateB> {
  a = a ?? {}
  b = b ?? {}

  // Obtient les clées
  const stateKeys = objectMergeKeys(a, b, options?.defaultA ?? {}, options?.defaultB ?? {}).filter((key) =>
    key.startsWith('$'),
  )

  // affecte les clées de state
  let state = stateKeys.reduce((newState, key) => {
    ;(newState as any)[key] =
      key in a
        ? (a as any)[key]
        : key in b
          ? (b as any)[key]
          : key in defaultState
            ? (defaultState as any)[key]
            : undefined
    return newState
  }, {}) as ModelState<TModel, TStateA & TStateB>

  // effectue un calcul si demandé
  if (options?.compute) state = { ...state, ...options.compute(state) }

  // affecte au state les membres de l'objet a et b
  if (Array.isArray(a) || Array.isArray(b)) {
    const resultArray: object[] = []
    for (let index = 0; index < Math.max((a as any)?.length ?? 0, (a as any)?.length ?? 0); index++) {
      const itemModelState = mergeModelStateInt((a as any)[index], (b as any)[index], defaultState, options)
      // if (options?.compute) itemModelState = Object.assign(itemModelState, options.compute(itemModelState))
      resultArray.push(itemModelState)
      if (options?.summarize) state = { ...state, ...options.summarize(state, itemModelState) }
    }
    return Object.assign([...resultArray], state) as ModelState<TModel, TStateA & TStateB>
  }

  const abOtherKeys = objectMergeKeys(a, b).filter((key) => !key.startsWith('$'))
  for (const key of abOtherKeys) {
    const itemModelState = mergeModelStateInt((a as any)[key], (b as any)[key], defaultState, options)
    // if (options?.compute) itemModelState = Object.assign(itemModelState, options.compute(itemModelState))
    ;(state as any)[key] = itemModelState
    if (options?.summarize) state = Object.assign(state, options.summarize(state, itemModelState))
  }
  return state
}

export function reshapeModelState<TModel extends object, TState extends object>(
  state: ModelState<TModel, TState>,
  defaultStateObject: TState,
): ModelState<TModel, TState> {
  // Obtient les clées depuis le default
  const stateKeys = Object.keys(defaultStateObject).filter((key) => key.startsWith('$'))
  // Reshape l'objet
  return reshapeModelStateInt(state, defaultStateObject, stateKeys)
}

function reshapeModelStateInt<TModel extends object, TState extends object>(
  state: ModelState<TModel, TState>,
  defaultStateObject: TState,
  defaultStateKeys: string[],
): ModelState<TModel, TState> {
  // affecte les clées de state
  const currentState = defaultStateKeys.reduce((newState, key) => {
    ;(newState as any)[key] = key in state ? (state as any)[key] : (defaultStateObject as any)[key]
    return newState
  }, {}) as ModelState<TModel, TState>

  // affecte à currentState les membres de l'objet state
  if (Array.isArray(state)) {
    const resultArray: object[] = []
    for (let index = 0; index < (state as any).length; index++) {
      const itemModelState = reshapeModelStateInt(state[index], defaultStateObject, defaultStateKeys)
      resultArray.push(itemModelState)
    }
    return Object.assign([...resultArray], currentState) as ModelState<TModel, TState>
  }

  const stateOtherKeys = Object.keys(state).filter((key) => !key.startsWith('$'))
  for (const key of stateOtherKeys) {
    const itemModelState = reshapeModelStateInt((state as any)[key], defaultStateObject, defaultStateKeys)
    ;(currentState as any)[key] = itemModelState
  }

  return currentState
}

